import { useCallback, useEffect, useRef, useState } from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";

import StationStateApi, {
  getStationStateHubUrl,
} from "../services/StationStateApi";
import {
  StationState,
  normalizeStationState,
} from "../services/stationState.types";

export type LiveConnectionStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected";

export interface UseStationStateResult {
  /** Último estado recebido (REST inicial ou push do hub). */
  state: StationState | null;
  /** Epoch (ms) de quando `state` foi recebido — base para interpolar o tempo decorrido. */
  receivedAt: number;
  /** Epoch (ms) que avança a cada segundo (congela quando pausado) — dispara o re-render do tempo. */
  now: number;
  /** Estado da conexão em tempo real. */
  status: LiveConnectionStatus;
  /** Mensagem de erro amigável quando a via realtime falha (o último estado segue visível). */
  error: string | null;
}

/**
 * Assina o estado de ocupação da estação em tempo real.
 *
 * - Faz um GET inicial (paint imediato + fallback do hub).
 * - Conecta no hub SignalR `/hubs/station-state`; o servidor envia o estado corrente
 *   ao conectar (Clients.Caller) e empurra `StateChanged` a cada mudança (AC-4).
 * - Conta `elapsedSeconds` localmente entre pushes (tick de 1s) e re-sincroniza a cada
 *   push, espelhando o padrão descrito no design do backend.
 * - `paused`: congela a aplicação de pushes e o tick (botão "Pausar").
 */
export const useStationState = (paused: boolean): UseStationStateResult => {
  const [state, setState] = useState<StationState | null>(null);
  const [receivedAt, setReceivedAt] = useState<number>(() => Date.now());
  const [now, setNow] = useState<number>(() => Date.now());
  const [status, setStatus] = useState<LiveConnectionStatus>("connecting");
  const [error, setError] = useState<string | null>(null);

  const pausedRef = useRef(paused);
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  const applyState = useCallback((raw: any) => {
    if (pausedRef.current) return; // congela a atualização enquanto pausado
    const normalized = normalizeStationState(raw);
    if (!normalized) return;
    setState(normalized);
    const ts = Date.now();
    setReceivedAt(ts);
    setNow(ts);
  }, []);

  // Paint inicial / fallback via REST — o GET permanece como contrato de leitura.
  useEffect(() => {
    let alive = true;
    StationStateApi.GetStationState()
      .then((res: any) => {
        if (!alive) return;
        const data = res?.data ?? res;
        if (data) applyState(data);
      })
      .catch(() => {
        /* silencioso: o hub é a via primária; o erro do hub é quem reporta */
      });
    return () => {
      alive = false;
    };
  }, [applyState]);

  // Tempo real via SignalR.
  useEffect(() => {
    let disposed = false;
    let connection: HubConnection;

    try {
      connection = new HubConnectionBuilder()
        .withUrl(getStationStateHubUrl())
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();
    } catch {
      setStatus("disconnected");
      setError("Não foi possível iniciar a conexão em tempo real.");
      return;
    }

    connection.on("StateChanged", (payload: any) => applyState(payload));
    connection.onreconnecting(() => setStatus("reconnecting"));
    connection.onreconnected(() => {
      setStatus("connected");
      setError(null);
    });
    connection.onclose(() => {
      if (!disposed) setStatus("disconnected");
    });

    setStatus("connecting");
    connection
      .start()
      .then(() => {
        if (!disposed) {
          setStatus("connected");
          setError(null);
        }
      })
      .catch(() => {
        if (!disposed) {
          setStatus("disconnected");
          setError(
            "Sem conexão em tempo real — exibindo o último estado conhecido."
          );
        }
      });

    return () => {
      disposed = true;
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch(() => {});
      }
    };
  }, [applyState]);

  // Tick local de 1s para interpolar o tempo decorrido entre pushes.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!pausedRef.current) setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  return { state, receivedAt, now, status, error };
};

export default useStationState;
